// client/src/coms/soundboard/soundStore.js
// Device-local storage (IndexedDB) for the user's own clips and for clips received from others.
// Falls back to memory when IndexedDB is unavailable (private windows), so playback still works.
// Nothing here is shared: other people only ever receive a clip through the server.

const DB_NAME = 'voxemera-soundboard';
// Version 2 also recreates stores missing from a database that was opened without them.
const DB_VERSION = 2;
const LOCAL = 'local'; // my clips: { id, name, emoji, hash, wav: Blob, volume, shortcut, favorite, group, position, created_at }
const CACHE = 'cache'; // received clips: { hash, wav: Blob, size, cached_at, last_used_at }
const MAX_CACHE_BYTES = 40 * 1024 * 1024;

let dbPromise = null;
const memory = { [LOCAL]: new Map(), [CACHE]: new Map() };

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(LOCAL)) db.createObjectStore(LOCAL, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(CACHE)) db.createObjectStore(CACHE, { keyPath: 'hash' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

async function tx(store, mode, fn) {
  const db = await openDb();
  if (!db) return fn(null);
  return new Promise((resolve, reject) => {
    let result;
    const t = db.transaction(store, mode);
    const os = t.objectStore(store);
    Promise.resolve(fn(os)).then((r) => {
      result = r;
    });
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

const req2p = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

async function getAll(store) {
  return tx(store, 'readonly', async (os) => (os ? req2p(os.getAll()) : Array.from(memory[store].values())));
}

async function put(store, value) {
  return tx(store, 'readwrite', async (os) => {
    if (!os) return memory[store].set(value.id || value.hash, value);
    return req2p(os.put(value));
  });
}

async function getOne(store, key) {
  return tx(store, 'readonly', async (os) => (os ? req2p(os.get(key)) : memory[store].get(key)));
}

async function remove(store, key) {
  return tx(store, 'readwrite', async (os) => (os ? req2p(os.delete(key)) : memory[store].delete(key)));
}

export async function listLocalSounds() {
  const all = (await getAll(LOCAL)) || [];
  return all.sort((a, b) => (a.position || 0) - (b.position || 0) || String(a.created_at).localeCompare(String(b.created_at)));
}

export async function saveLocalSound(sound) {
  const value = { volume: 1, favorite: false, shortcut: '', group: 'General', emoji: '', position: Date.now(), created_at: new Date().toISOString(), ...sound };
  if (!value.id) value.id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  await put(LOCAL, value);
  return value;
}

export async function updateLocalSound(id, patch) {
  const cur = await getOne(LOCAL, id);
  if (!cur) return null;
  const next = { ...cur, ...patch, id };
  await put(LOCAL, next);
  return next;
}

export async function deleteLocalSound(id) {
  await remove(LOCAL, id);
}

// Clips are cached under their server id (the normalised file everyone hears).
export async function getCachedClip(hash) {
  const row = await getOne(CACHE, hash);
  if (!row) return null;
  put(CACHE, { ...row, last_used_at: Date.now() }).catch(() => {});
  return row.wav;
}

export async function cacheClip(hash, wavBlob) {
  await put(CACHE, { hash, wav: wavBlob, size: wavBlob.size, cached_at: Date.now(), last_used_at: Date.now() });
  // Keep the cache bounded: drop least recently used clips.
  const all = (await getAll(CACHE)) || [];
  let total = all.reduce((n, r) => n + (r.size || 0), 0);
  if (total <= MAX_CACHE_BYTES) return;
  for (const r of all.sort((a, b) => (a.last_used_at || 0) - (b.last_used_at || 0))) {
    if (total <= MAX_CACHE_BYTES) break;
    await remove(CACHE, r.hash);
    total -= r.size || 0;
  }
}

export async function cacheStats() {
  const all = (await getAll(CACHE)) || [];
  return { count: all.length, bytes: all.reduce((n, r) => n + (r.size || 0), 0) };
}

export async function clearClipCache() {
  const all = (await getAll(CACHE)) || [];
  for (const r of all) await remove(CACHE, r.hash);
}
